import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Typography,
  Button,
  Space,
  message,
  Spin,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Skeleton,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import "./styles.css";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

// Local Device interface definition
interface Device {
  id: number;
  deviceCode: string;
  deviceName: string;
  data: any;
  isPay: number;
  project: number | { id: number; name: string };
  addTime: string;
  deviceType: number;
  thirdType: string;
  area: number | { id: number; title: string };
  beian: string;
  exData: {
    driverName?: string;
    idcardNumber?: string;
    monitorKey?: string;
    channel?: any[];
    [key: string]: any;
  };
  deviceFactory: string;
  latitude: string;
  longitude: string;
  uid: string | null;
  price: number | null | string;
  isDel: number;
  remark: string | null;
}

// Local deviceTypeMap definition
const deviceTypeMap: Record<number, string> = {
  0: "塔吊",
  1: "扬尘",
  3: "视频",
  4: "卸料平台",
  5: "施工升降机",
};

// Define API response interface with pagination structure
interface ApiResponseWithPagination<T> {
  code: number;
  msg: string;
  data?: {
    items: T[];
    total: number;
    page: number;
    limit: number;
  };
}

const DeviceQuery: React.FC = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedDeviceType, setSelectedDeviceType] = useState<number | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [stats, setStats] = useState({
    totalDevices: 0,
    paidDevices: 0,
    unpaidDevices: 0,
  });

  // Fetch devices with pagination
  const fetchDevices = async (
    page: number = 1,
    limit: number = 10,
    search?: string,
    deviceType?: number | null,
  ) => {
    try {
      setLoading(true);

      // Prepare query parameters
      const params: Record<string, any> = { page, limit };

      const effectiveSearch = search !== undefined ? search : searchValue;
      const effectiveType = deviceType !== undefined ? deviceType : selectedDeviceType;

      if (effectiveType !== null) {
        params.deviceType = effectiveType;
      }

      if (effectiveSearch) {
        params.search = effectiveSearch;
      }

      const response = await api.get<ApiResponseWithPagination<Device>>('/devices', { params });

      if (response.data.code === 0 && response.data.data) {
        const { items, total } = response.data.data;
        setDevices(items);
        setPagination(prev => ({
          ...prev,
          current: page,
          total,
        }));
        // Update stats based on current page data
        setStats({
          totalDevices: total,
          paidDevices: items.filter(d => d.isPay === 1).length,
          unpaidDevices: items.filter(d => d.isPay !== 1).length,
        });
      } else {
        message.error("获取设备列表失败");
      }
    } catch (error) {
      console.error("Error fetching devices:", error);
      message.error("获取设备列表时出错");
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchDevices();
  }, []);

  // Handle search
  const handleSearch = () => {
    fetchDevices(1, pagination.pageSize, searchValue, selectedDeviceType);
  };

  // Handle device type filter change
  const handleDeviceTypeChange = (value: number | null) => {
    setSelectedDeviceType(value);
    fetchDevices(1, pagination.pageSize, searchValue, value);
  };

  // Handle table pagination change
  const handleTableChange = (pag: any) => {
    fetchDevices(pag.current, pag.pageSize, searchValue, selectedDeviceType);
  };

  // Handle reset filters
  const handleResetSearch = () => {
    setSearchValue("");
    setSelectedDeviceType(null);
    fetchDevices(1, pagination.pageSize, "", null);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchDevices(pagination.current, pagination.pageSize, searchValue, selectedDeviceType);
  };

  // Table columns
  const columns = [
    {
      title: "设备ID",
      dataIndex: "deviceCode",
      key: "deviceCode",
      render: (text: string) => (
        <a onClick={() => handleSearch()}>{text}</a>
      ),
    },
    {
      title: "设备名称",
      dataIndex: "deviceName",
      key: "deviceName",
      render: (text: string) => text || "-",
    },
    {
      title: "设备类型",
      dataIndex: "deviceType",
      key: "deviceType",
      render: (type: number) => (
        <Tag color="blue">{deviceTypeMap[type] || "未知类型"}</Tag>
      ),
    },
    {
      title: "所属项目",
      key: "project",
      render: (_: any, record: Device) => {
        const projectName =
          typeof record.project === 'object' && record.project !== null
            ? record.project.name
            : '未知项目';
        const projectId = typeof record.project === 'object' && record.project !== null
          ? record.project.id
          : record.project;

        return (
          <a
            onClick={() => navigate(`/devices/${projectId}`, {
              state: {
                projectName: typeof record.project === 'object' ? record.project.name : projectName
              }
            })}
          >
            {projectName}
          </a>
        );
      },
    },
    {
      title: "添加时间",
      dataIndex: "addTime",
      key: "addTime",
      render: (time: string) => new Date(time).toLocaleString(),
    },
    {
      title: "支付状态",
      key: "isPay",
      render: (_: any, record: Device) => (
        <Tag color={record.isPay === 1 ? "green" : "red"}>
          {record.isPay === 1 ? "已支付" : "未支付"}
        </Tag>
      ),
    },
    {
      title: "操作",
      key: "action",
      render: (_: any, record: Device) => {
        const projectId = typeof record.project === 'object' && record.project !== null
          ? record.project.id
          : record.project;

        return (
          <Space size="middle">
            <a onClick={() => window.open(`/devices/${projectId}`, '_blank')}>查看详情</a>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="device-query-container">
      <Card className="device-query-card">
        <div className="device-query-header">
          <Title level={2}>设备查询</Title>
          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              刷新
            </Button>
          </Space>
        </div>

        {/* Statistics Section */}
        <div className="device-query-stats-section">
          <div className="stats-card">
            <div className="stat-item">
              <div className="stat-icon-wrapper">
                <DatabaseOutlined className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.totalDevices}</div>
                <div className="stat-title">设备总数</div>
              </div>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <div className="stat-icon-wrapper">
                <CheckCircleOutlined className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.paidDevices}</div>
                <div className="stat-title">已支付</div>
              </div>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <div className="stat-icon-wrapper">
                <CloseCircleOutlined className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.unpaidDevices}</div>
                <div className="stat-title">未支付</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[16, 12]} align="middle">
            <Col xs={24} sm={12} md={6} lg={5}>
              <Search
                placeholder="搜索设备ID或名称"
                allowClear
                enterButton={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={handleSearch}
              />
            </Col>
            <Col xs={24} sm={12} md={5} lg={4}>
              <Select
                placeholder="设备类型筛选"
                allowClear
                value={selectedDeviceType}
                onChange={handleDeviceTypeChange}
                style={{ width: '100%' }}
              >
                {Object.entries(deviceTypeMap).map(([key, value]) => (
                  <Option key={key} value={parseInt(key)}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <Space className="device-query-filter-actions" wrap>
                <Button icon={<FilterOutlined />} onClick={handleResetSearch}>
                  重置筛选
                </Button>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  查询
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Spin spinning={loading}>
          {loading && devices.length === 0 ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : (
            <Table
              dataSource={devices}
              columns={columns}
              rowKey="id"
              onChange={handleTableChange}
              scroll={{ x: 'max-content' }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                simple: window.innerWidth <= 576,
              }}
              size="small"
            />
          )}
        </Spin>

        <div className="device-query-platform-footer">
          <Text type="secondary">智趣智慧工地数据管理平台</Text>
          <br />
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
            辽ICP备12014462号-7
          </a>
        </div>
      </Card>
    </div>
  );
};

export default DeviceQuery;
